import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api } from "@/lib/api";
import authService from "@/services/auth.service";
import { toast } from "sonner";
import { Lock, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { PageTransition } from "@/components/PageTransition";
import { isAdmin } from "@/lib/utils";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFirstTime = searchParams.get("firstTime") === "true";
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      setError("New password must be different from current password");
      return;
    }

    setIsLoading(true);
    try {
      // Use the unified change-password endpoint which handles both members and teachers
      await api.post("/auth/change-password", {
        currentPassword,
        newPassword,
        teacherId: currentUser.id, // This will be ignored for members, used for teachers
      });

      toast.success("Password changed successfully!");

      // Refresh user data from server to reflect updated password state
      try {
        const refreshedUser = await authService.getCurrentUser();
        if (refreshedUser) {
          localStorage.setItem('user', JSON.stringify({ ...refreshedUser, requiresPasswordChange: false }));
        } else {
          const updatedUser = { ...currentUser, requiresPasswordChange: false };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } catch {
        const updatedUser = { ...currentUser, requiresPasswordChange: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      // If first time login, redirect to dashboard
      if (isFirstTime) {
        setTimeout(() => {
          const userIsAdmin = isAdmin(currentUser);
          window.location.href = userIsAdmin ? "/dashboard" : "/member/dashboard";
        }, 1000);
      } else {
        navigate("/dashboard");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to change password";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#fffffb' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Lock className="w-6 h-6 text-[#4dbd07]" />
                {isFirstTime ? "Change Your Password" : "Change Password"}
              </CardTitle>
              <CardDescription>
                {isFirstTime 
                  ? "For security reasons, please change your default password to a secure one."
                  : "Update your password to keep your account secure."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isFirstTime && (
                <Alert variant="warning" className="mb-4">
                  <AlertDescription>
                    You are using the default password. Please change it now for security.
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="danger" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">
                    {isFirstTime ? "Current Password (Default: 123456)" : "Current Password"}
                  </Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    required
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 6 characters long
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#4dbd07] hover:bg-[#3fa006] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? "Changing Password..." : "Change Password"}
                </Button>

                {!isFirstTime && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate("/dashboard")}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageTransition>
  );
}

